import { type IconType, Pill } from "@clipboard-health/ui-components";
import { type SxProps, type Theme, useTheme } from "@mui/material/styles";

import { type PaymentsListItem } from "../types/PaymentsListItem";
import { isReversalPaymentItem } from "../utils/isReversalPaymentItem";

export interface PaymentsListItemPillProps {
  item: PaymentsListItem;
  /** Whether to reduce the Pill's height
   * Default: `true`
   */
  compact?: boolean;
}

export function PaymentsListItemPill(props: PaymentsListItemPillProps) {
  const { compact = true, item } = props;
  const theme = useTheme();
  const icon = getIcon(theme, item);

  return (
    <Pill
      variant="filled"
      size="small"
      backgroundColor={(theme) => getPillBackgroundColor(theme, item)}
      color={(theme) => getPillTextColor(theme, item)}
      label={getPillText(item)}
      iconType={icon?.type}
      iconColor={(_) => icon?.color}
      sx={(theme) => ({
        height: compact ? theme.spacing(6) : undefined,
        minHeight: compact ? theme.spacing(6) : undefined,
        paddingX: theme.spacing(1),
        "& .MuiSvgIcon-root": icon?.style,
      })}
    />
  );
}

function getPillText(item: PaymentsListItem): string {
  if (isReversalPaymentItem(item)) {
    return "Reversal";
  }

  switch (item.attributes.payoutStatus) {
    case "paid":
    case undefined: {
      return "Paid";
    }

    case "pending": {
      return "In transit";
    }

    case "failed": {
      return "Failed payout";
    }

    default: {
      return item.attributes.payoutStatus satisfies never;
    }
  }
}

function getPillBackgroundColor(theme: Theme, item: PaymentsListItem): string {
  if (isReversalPaymentItem(item)) {
    return theme.palette.intent?.warning.background ?? "";
  }

  switch (item.attributes.payoutStatus) {
    case "paid":
    case undefined: {
      return theme.palette.intent?.success.background ?? "";
    }

    case "pending": {
      return theme.palette.intent?.neutral.background ?? "";
    }

    case "failed": {
      return theme.palette.intent?.error.background ?? "";
    }

    default: {
      return item.attributes.payoutStatus satisfies never;
    }
  }
}

function getPillTextColor(theme: Theme, item: PaymentsListItem): string {
  if (isReversalPaymentItem(item)) {
    return theme.palette.intent?.warning.text ?? "";
  }

  switch (item.attributes.payoutStatus) {
    case "paid":
    case undefined: {
      return theme.palette.intent?.success.text ?? "";
    }

    case "pending": {
      return theme.palette.intent?.neutral.text ?? "";
    }

    case "failed": {
      return theme.palette.intent?.error.text ?? "";
    }

    default: {
      return item.attributes.payoutStatus satisfies never;
    }
  }
}

function getIcon(
  theme: Theme,
  item: PaymentsListItem
):
  | {
      type: IconType;
      color?: string;
      style?: SxProps<Theme>;
    }
  | undefined {
  const style: SxProps<Theme> = {
    width: theme.spacing(4),
    height: theme.spacing(4),
    marginLeft: theme.spacing(1),
  };

  if (item.attributes.payoutStatus === "paid" && item.attributes.payoutMethod === "instant") {
    return {
      type: "bolt-filled",
      color: theme.palette.intent?.success.text,
      style,
    };
  }

  if (item.attributes.payoutStatus === "pending") {
    return {
      type: "clock",
      color: theme.palette.intent?.neutral.text,
      style,
    };
  }

  if (item.attributes.payoutStatus === "failed") {
    return { type: "triangle-exclamation", color: theme.palette.intent?.error.text, style };
  }

  return undefined;
}
